import {dropRight, find} from 'lodash';
import {Fragment, useCallback, useContext, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {AssignmentSelectors, Icon, IconName, Input, Select, Tooltip, TreeRenderProps, UserSelector} from 'components';
import {UserContext} from 'context';
import {Group, Option} from 'interfaces';
import {TRANSLATION_KEYS} from 'locale';
import {getAssignmentName} from 'services';
import {InteractiveChatAction, InteractiveChatItem, NodePath, UserSummary} from 'types';
import {interactiveChatUtils} from 'utils/interactive-chat';
import {buildTranslationKey, getUserName} from 'utils/transform';

import {LLM_CHATBOT_ACTIONS, NODE_CHILD_ACTIONS, ROOT_NODES_ACTIONS} from '../constants';

const T_KEYS = TRANSLATION_KEYS.USER.CONTENT.CLIENT_SESSION.INTERACTIVE_CHAT;
const T_KEYS_ASYNC_REQUEST = TRANSLATION_KEYS.COMMON.SESSION.ASYNC_REQUEST.TYPE;

type Props = {
    users: UserSummary[];
    groups: Group[];
    interactiveChatOptions: Option[];
    asyncRequestOptions: Option[];
    canSelectAgent: boolean;
    getNodeAction: (path: NodePath) => InteractiveChatAction;
} & TreeRenderProps<InteractiveChatItem>

export const InteractiveChatNode: React.FC<Props> = ({
    value, path, getNodeAction, isEdit, users, groups, interactiveChatOptions, asyncRequestOptions, canSelectAgent, onNodeValueChange
}) => {
    const {t} = useTranslation();
    const {
        settings: {
            featuresAvailability: {
                requestAppointment: requestAppointmentEnabled,
                chatGPT: chatGPTEnabled,
                claude: claudeEnabled,
                mastra: mastraEnabled,
                watsonx: watsonxEnabled,
                internalLiveAgent: internalLiveAgentEnabled,
            }
        }
    } = useContext(UserContext);

    const selectedAssignmentInfo = useMemo(() => value.actionData?.assignment, [value]);
    const selectedGroup = useMemo(() => find(groups, {id: selectedAssignmentInfo?.groupId}), [groups, selectedAssignmentInfo?.groupId]);
    const parentAction = useMemo(() => getNodeAction(dropRight(path)), [path, getNodeAction]);

    const selectedUserEmail = useMemo(() => value.actionData?.agentEmail, [value.actionData?.agentEmail]);
    const selectedUser = useMemo(() => find(users, {email: selectedUserEmail}), [users, selectedUserEmail]);
    const selectedUserName = useMemo(() => selectedUser ? getUserName(t, selectedUser) : null, [t, selectedUser]);

    const isRootNode = useMemo(() => path.length === 1, [path]);
    const isBackActionAvailable = useMemo(() => path.length > 2, [path]);

    const availableActionOptions = useMemo(() =>
        isRootNode ? ROOT_NODES_ACTIONS : NODE_CHILD_ACTIONS[parentAction] || [], [isRootNode, parentAction]);

    const selectedInteractiveChatId = useMemo(() => value.actionData?.interactiveChatId, [value.actionData?.interactiveChatId]);
    const selectedInteractiveChatName = useMemo(() =>
        find(interactiveChatOptions, {id: selectedInteractiveChatId})?.name, [interactiveChatOptions, selectedInteractiveChatId]);

    const getTranslatedAction = useCallback((action: InteractiveChatAction) => {
        const tKey = buildTranslationKey(action);
        return T_KEYS.ACTIONS[tKey] ? t(T_KEYS.ACTIONS[tKey]) : action;
    }, [t]);

    const actionOptions = useMemo(() => availableActionOptions
        .filter((action) => requestAppointmentEnabled || action !== InteractiveChatAction.RequestAppointment)
        .filter((action) => chatGPTEnabled || action !== InteractiveChatAction.ChatGpt)
        .filter((action) => claudeEnabled || action !== InteractiveChatAction.Claude)
        .filter((action) => mastraEnabled || action !== InteractiveChatAction.Mastra)
        .filter((action) => watsonxEnabled || action !== InteractiveChatAction.Watsonx)
        .filter((action) => internalLiveAgentEnabled || action !== InteractiveChatAction.InternalLiveAgent)
        .filter((action) => isBackActionAvailable || action !== InteractiveChatAction.Back)
        .map((action) =>
            interactiveChatUtils.buildInteractiveChatOption({id: action, value: {action, text: getTranslatedAction(action)}})
        ), [
        availableActionOptions, requestAppointmentEnabled, chatGPTEnabled, claudeEnabled, mastraEnabled, watsonxEnabled,
        internalLiveAgentEnabled, isBackActionAvailable, getTranslatedAction
    ]);

    const actionIcon: IconName = useMemo(() => interactiveChatUtils.getInteractiveChatActionIcon(value.action), [value.action]);

    const renderAssignment = useCallback(() => {
        const assignment = getAssignmentName(groups, selectedAssignmentInfo?.groupId, selectedAssignmentInfo?.subgroupId);
        return (
            <span>{` ${assignment}`}</span>
        );
    }, [groups, selectedAssignmentInfo]);

    const renderActionParams = useCallback(() => (
        <div className='tw-flex tw-items-center -md:tw-mt-2 md:tw-ml-4'>
            {value.action === InteractiveChatAction.CallAgent && selectedUserName && canSelectAgent &&
                <span>{` (${selectedUserName})`}</span>}
            {selectedGroup && value.action === InteractiveChatAction.Reassign && renderAssignment()}
            {value.action === InteractiveChatAction.InteractiveChat && selectedInteractiveChatName &&
                <span>{` (${selectedInteractiveChatName})`}</span>}
            {value.action === InteractiveChatAction.AsyncRequest && value.actionData?.asyncRequest?.typeName &&
                <span>
                    {` (${t(T_KEYS_ASYNC_REQUEST[buildTranslationKey(value.actionData.asyncRequest.typeName)])})`}
                </span>}
        </div>
    ), [value.action, selectedUserName, selectedGroup, selectedInteractiveChatName,
        canSelectAgent, renderAssignment, t, value.actionData?.asyncRequest?.typeName]);

    if (isEdit) {
        return (
            <div className='tw-flex md:tw-flex-wrap md:tw-items-center -md:tw-flex-col -md:tw-mr-2'>
                <Select
                    className='md:tw-mr-2 tw-w-full md:tw-w-48'
                    value={value.action}
                    valueAs='id'
                    options={actionOptions}
                    placeholder={t(T_KEYS.ACTION)}
                    onChange={(action) => onNodeValueChange({action}, path)}
                />
                {isRootNode &&
                    <Input
                        value={value.name}
                        label={t(T_KEYS.NAME)}
                        className='md:tw-mx-2 md:tw-w-48'
                        onChange={(name) => onNodeValueChange({name}, path)}
                    />}
                <Input
                    value={value.text}
                    label={t(T_KEYS.MESSAGE)}
                    className='md:tw-mx-2 tw-w-full md:tw-w-48'
                    onChange={(text) => onNodeValueChange({text}, path)}
                />
                {value.action === InteractiveChatAction.CallAgent &&
                    !LLM_CHATBOT_ACTIONS.includes(parentAction) && canSelectAgent &&
                        <UserSelector
                            value={selectedUserEmail}
                            users={users}
                            className='md:tw-mx-2 tw-w-full md:tw-w-48'
                            context='agent'
                            field='email'
                            onChange={(agentEmail) => onNodeValueChange({actionData: {agentEmail}}, path)}
                        />}
                {value.action === InteractiveChatAction.Reassign &&
                    <AssignmentSelectors
                        groups={groups}
                        selectedGroupId={selectedAssignmentInfo?.groupId}
                        selectedSubgroupId={selectedAssignmentInfo?.subgroupId}
                        onChange={(groupId, subgroupId) => onNodeValueChange({actionData: {assignment: {groupId, subgroupId}}}, path)}
                    >
                        {({renderGroup, renderSubgroup}) => (
                            <Fragment>
                                <div className='tw-w-full md:tw-w-48 md:tw-mx-2 tw-flex-shrink-1'>{renderGroup()}</div>
                                <div className='tw-w-full md:tw-w-48 md:tw-mx-2 tw-flex-shrink-1'>{renderSubgroup()}</div>
                            </Fragment>
                        )}
                    </AssignmentSelectors>}
                {value.action === InteractiveChatAction.AsyncRequest &&
                    <Fragment>
                        <Select
                            value={value.actionData?.asyncRequest?.typeName}
                            options={asyncRequestOptions}
                            valueAs='id'
                            placeholder={t(T_KEYS.ACTIONS.ASYNC_REQUEST)}
                            className='tw-w-full md:tw-w-48'
                            onChange={(id) => onNodeValueChange({
                                actionData: {
                                    asyncRequest: {
                                        typeName: id,
                                        condition: value.actionData?.asyncRequest?.condition || ''
                                    }
                                }
                            }, path)}
                        />
                        {LLM_CHATBOT_ACTIONS.includes(parentAction) &&
                            <Input
                                value={value.actionData?.asyncRequest?.condition || ''}
                                label={t(T_KEYS.CONDITION)}
                                className='md:tw-mx-2 tw-w-full md:tw-w-48'
                                onChange={(condition) => onNodeValueChange({
                                    actionData: {
                                        asyncRequest: {
                                            typeName: value.actionData?.asyncRequest?.typeName,
                                            condition
                                        }
                                    }
                                }, path)}
                            />}
                    </Fragment>}
                {value.action === InteractiveChatAction.InteractiveChat &&
                    <Select
                        options={interactiveChatOptions}
                        value={value.actionData?.interactiveChatId}
                        valueAs='id'
                        placeholder={t(T_KEYS.INTERACTIVE_CHAT)}
                        className='md:tw-mx-2 tw-w-full md:tw-w-48'
                        onChange={(id) => onNodeValueChange({actionData: {interactiveChatId: id}}, path)}
                    />}
                {!isRootNode && value.action !== InteractiveChatAction.RequestAppointment && <Input
                    label={t(T_KEYS.REPLY_MESSAGE)}
                    value={value.replyMessage}
                    onChange={(replyMessage) => onNodeValueChange({replyMessage}, path)}
                />}
            </div>
        );
    }

    return (
        <div className='tw-flex md:tw-items-center -md:tw-flex-col'>
            <Tooltip message={getTranslatedAction(value.action)}>
                <Icon name={actionIcon} className='tw-mr-1' />
            </Tooltip>
            {renderActionParams()}
            {value.name && <div className='tw-ml-2 tw-font-bold'>{value.name}</div>}
            <div className='tw-ml-2'>{value.text}</div>
            {value.replyMessage && !isRootNode && value.action !== InteractiveChatAction.RequestAppointment &&
                <div className='tw-flex tw-items-center md:tw-ml-4 -md:tw-mt-2'>
                    <Icon name='reply' size='sm' className='tw-mr-2' />
                    {value.replyMessage}
                </div>}
        </div>
    );
};